import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  GripVertical, 
  Edit, 
  Eye, 
  EyeOff, 
  Calendar, 
  Loader2, 
  ChevronUp, 
  ChevronDown,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useLandingSectionsAdmin, type LandingSection } from '@/hooks/useLandingSections';
import { SectionEditor } from './SectionEditor';

export function SectionsManager() {
  const { sections, isLoading, error, updateSection, reorderSections, refetch } = useLandingSectionsAdmin();
  const [editingSection, setEditingSection] = useState<LandingSection | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  const handleVisibilityToggle = async (section: LandingSection) => {
    try {
      setSavingId(section.id);
      await updateSection(section.id, { is_visible: !section.is_visible });
      toast.success(`${section.name} ${section.is_visible ? 'ausgeblendet' : 'eingeblendet'}`);
    } catch (err) {
      toast.error('Fehler beim Aktualisieren');
      console.error(err);
    } finally {
      setSavingId(null);
    }
  };

  const handleDateChange = async (section: LandingSection, field: 'visible_from' | 'visible_until', value: string) => {
    try {
      setSavingId(section.id);
      await updateSection(section.id, { [field]: value || null });
      toast.success('Datum aktualisiert');
    } catch (err) {
      toast.error('Fehler beim Aktualisieren');
      console.error(err);
    } finally {
      setSavingId(null);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newOrder = [...sections];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    try {
      await reorderSections(newOrder.map(s => s.id));
      toast.success('Reihenfolge aktualisiert');
    } catch (err) {
      toast.error('Fehler beim Sortieren');
      console.error(err);
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === sections.length - 1) return;
    const newOrder = [...sections];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    try {
      await reorderSections(newOrder.map(s => s.id));
      toast.success('Reihenfolge aktualisiert');
    } catch (err) {
      toast.error('Fehler beim Sortieren');
      console.error(err);
    }
  };

  const handleContentSave = async (id: string, content: Record<string, unknown>) => {
    await updateSection(id, { content });
  };

  const isCurrentlyVisible = (section: LandingSection) => {
    if (!section.is_visible) return false;
    const today = new Date().toISOString().split('T')[0];
    if (section.visible_from && section.visible_from > today) return false;
    if (section.visible_until && section.visible_until < today) return false;
    return true;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={() => refetch()}>Erneut versuchen</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Landing-Page Sektionen
          </CardTitle>
          <CardDescription>
            Verwalte Sichtbarkeit, Reihenfolge und Inhalte der Landing-Page-Sektionen
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-end mb-4">
            <Button variant="outline" asChild>
              <a href="/" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Vorschau öffnen
              </a>
            </Button>
          </div>

          <div className="space-y-3">
            {sections.map((section, index) => {
              const visible = isCurrentlyVisible(section);
              return (
                <div
                  key={section.id}
                  className={`border rounded-lg p-4 transition-colors ${
                    visible ? 'bg-card' : 'bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Drag handle and order controls */}
                    <div className="flex flex-col items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === sections.length - 1}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Section info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium truncate">{section.name}</h3>
                        {visible ? (
                          <Eye className="h-4 w-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <EyeOff className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{section.slug}</p>
                    </div>

                    {/* Visibility toggle */}
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`visible-${section.id}`} className="text-sm">
                        Aktiv
                      </Label>
                      <Switch
                        id={`visible-${section.id}`}
                        checked={section.is_visible}
                        onCheckedChange={() => handleVisibilityToggle(section)}
                        disabled={savingId === section.id}
                      />
                    </div>

                    {/* Date range */}
                    <div className="flex items-center gap-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Von</Label>
                        <Input
                          type="date"
                          value={section.visible_from || ''}
                          onChange={(e) => handleDateChange(section, 'visible_from', e.target.value)}
                          className="w-36 h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Bis</Label>
                        <Input
                          type="date"
                          value={section.visible_until || ''}
                          onChange={(e) => handleDateChange(section, 'visible_until', e.target.value)}
                          className="w-36 h-8 text-sm"
                        />
                      </div>
                    </div>

                    {/* Edit button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingSection(section)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Bearbeiten
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <SectionEditor
        section={editingSection}
        isOpen={!!editingSection}
        onClose={() => setEditingSection(null)}
        onSave={handleContentSave}
      />
    </div>
  );
}
